import React, { useMemo } from 'react';
import { MessageSquare, DollarSign, Truck, AlertCircle } from 'lucide-react';
import { formatCompactCurrency } from '../utils/formatCurrency';

/**
 * OrdersQuickStats - Compact analytics strip for the Orders screen
 * 
 * Shows 4 essential metrics at a glance:
 * - Unread customer messages
 * - Outstanding unpaid balance  
 * - Orders currently in transit
 * - Urgent escalations needing attention
 * 
 * Designed to sit between the page header and search/filter bar.
 * Clicking a stat can optionally trigger a filter or action.
 */
const OrdersQuickStats = ({ 
  receipts = [],
  unreadMessages = {},
  escalations = {},
  getPaymentStatus,
  stats = {},
  onStatClick,
  isMobile = false
}) => {

  const computed = useMemo(() => {
    // 1. Total unread customer messages across ALL receipts
    let totalNewMessages = 0;
    receipts.forEach(r => {
      const fromState = unreadMessages[r.id] || 0;
      const fromReceipt = (r.hasActiveChat && r.unreadMessages > 0) ? r.unreadMessages : 0;
      totalNewMessages += Math.max(fromState, fromReceipt);
    });

    // 2. Total unpaid balance across all receipts
    let totalUnpaidBalance = 0;
    let unpaidCount = 0;
    receipts.forEach(r => {
      if (getPaymentStatus) {
        const payment = getPaymentStatus(r);
        if (payment.status === 'unpaid' || payment.status === 'partial') {
          totalUnpaidBalance += payment.balance || 0;
          unpaidCount++;
        }
      }
    });

    // 3. Orders in transit (out for delivery)
    const inTransit = stats.out_for_delivery || 0;

    // 4. Unacknowledged escalations
    let escalationCount = 0;
    Object.values(escalations).forEach(esc => {
      if (esc && !esc.acknowledged) escalationCount++;
    });

    return { totalNewMessages, totalUnpaidBalance, unpaidCount, inTransit, escalationCount };
  }, [receipts, unreadMessages, escalations, getPaymentStatus, stats]);

  const { totalNewMessages, totalUnpaidBalance, unpaidCount, inTransit, escalationCount } = computed;

  // Don't render if there's absolutely nothing to show
  const hasAnything = totalNewMessages > 0 || totalUnpaidBalance > 0 || inTransit > 0 || escalationCount > 0;
  if (!hasAnything) return null;

  const statItems = [
    {
      key: 'messages',
      icon: MessageSquare,
      value: totalNewMessages,
      label: 'New Messages',
      show: totalNewMessages > 0,
      accent: 'blue',
      bgClass: 'bg-blue-50 border-blue-200',
      iconClass: 'text-blue-600',
      valueClass: 'text-blue-700',
      labelClass: 'text-blue-500',
      pulse: totalNewMessages > 0,
    },
    {
      key: 'escalations',
      icon: AlertCircle,
      value: escalationCount,
      label: 'Escalations',
      show: escalationCount > 0,
      accent: 'red',
      bgClass: 'bg-red-50 border-red-200',
      iconClass: 'text-red-500',
      valueClass: 'text-red-600',
      labelClass: 'text-red-400',
      pulse: true,
    },
    {
      key: 'unpaid',
      icon: DollarSign,
      value: formatCompactCurrency(totalUnpaidBalance),
      subValue: `${unpaidCount} order${unpaidCount !== 1 ? 's' : ''}`,
      label: 'Unpaid',
      show: totalUnpaidBalance > 0,
      accent: 'amber',
      bgClass: 'bg-amber-50 border-amber-200',
      iconClass: 'text-amber-600',
      valueClass: 'text-amber-700',
      labelClass: 'text-amber-500',
    },
    {
      key: 'transit',
      icon: Truck,
      value: inTransit,
      label: 'In Transit',
      show: inTransit > 0,
      accent: 'cyan',
      bgClass: 'bg-cyan-50 border-cyan-200',
      iconClass: 'text-cyan-600',
      valueClass: 'text-cyan-700',
      labelClass: 'text-cyan-500',
    },
  ].filter(s => s.show);

  if (statItems.length === 0) return null;

  // ── Mobile layout ──
  if (isMobile) {
    return (
      <div className="flex gap-2 px-4 py-2.5 overflow-x-auto no-scrollbar bg-slate-50 border-b border-slate-200">
        {statItems.map(stat => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.key}
              onClick={() => onStatClick?.(stat.key)}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border transition-all active:scale-[0.97] ${stat.bgClass}`}
            >
              <div className="relative">
                <Icon className={`w-4 h-4 ${stat.iconClass}`} />
                {stat.pulse && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-current animate-ping opacity-60" 
                    style={{ color: stat.accent === 'blue' ? '#2563eb' : '#ef4444' }} 
                  />
                )}
              </div>
              <span className={`text-sm font-bold ${stat.valueClass}`}>{stat.value}</span>
              <span className={`text-xs font-medium ${stat.labelClass}`}>{stat.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // ── Desktop layout ──
  return (
    <div className="flex items-center gap-3 mt-3 mb-1">
      {statItems.map((stat, i) => {
        const Icon = stat.icon;
        return (
          <React.Fragment key={stat.key}>
            <button
              onClick={() => onStatClick?.(stat.key)}
              className={`flex items-center gap-2.5 px-3.5 py-2 rounded-lg border transition-all hover:shadow-sm hover:scale-[1.01] active:scale-[0.99] ${stat.bgClass}`}
            >
              <div className="relative flex-shrink-0">
                <Icon className={`w-4 h-4 ${stat.iconClass}`} strokeWidth={2.2} />
                {stat.pulse && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-ping opacity-60"
                    style={{ backgroundColor: stat.accent === 'blue' ? '#2563eb' : '#ef4444' }}
                  />
                )}
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-base font-bold leading-none ${stat.valueClass}`}>
                  {stat.value}
                </span>
                <span className={`text-xs font-medium leading-none ${stat.labelClass}`}>
                  {stat.label}
                </span>
              </div>
              {stat.subValue && (
                <span className={`text-[10px] font-medium ${stat.labelClass} opacity-70 ml-0.5`}>
                  ({stat.subValue})
                </span>
              )}
            </button>
            {i < statItems.length - 1 && (
              <div className="w-px h-5 bg-slate-300 flex-shrink-0" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default OrdersQuickStats;